import { baseUrl, getTokenFromLocalStorage } from "./Configuration";

export interface SecurityDepositPerson {
  id: string;
  name: string;
  email: string;
  type: string;
  isReceiver: boolean;
  isGuarantor: boolean;
  lastSelectedReceiverId?: string | null;
}

export interface SecurityDepositEmailRequest {
  listingId: string;
  baseUrl: string;
}

export interface SelectReceiverRequest {
  votingPersonId: string;
  selectedPersonId: string;
}

export async function getPersonDetails(
  listingId: string,
  personId: string,
): Promise<SecurityDepositPerson> {
  const url = `${baseUrl}/api/SecurityDeposit/person-details/${listingId}/${personId}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new Error(errorData.error || "Failed to fetch person details");
    }

    return await response.json();
  } catch (error: any) {
    console.error("Error fetching person details:", error);
    if (error.message === "Failed to fetch") {
      throw new Error(
        "Cannot connect to server. Please ensure the backend is running.",
      );
    }
    throw error;
  }
}

export async function getPersonsByListing(
  listingId: string,
): Promise<SecurityDepositPerson[]> {
  const url = `${baseUrl}/api/SecurityDeposit/persons/${listingId}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new Error(errorData.error || "Failed to fetch persons");
    }

    return await response.json();
  } catch (error: any) {
    console.error("Error fetching persons:", error);
    if (error.message === "Failed to fetch") {
      throw new Error(
        "Cannot connect to server. Please ensure the backend is running.",
      );
    }
    throw error;
  }
}

export async function selectReceiver(
  listingId: string,
  votingPersonId: string,
  selectedPersonId: string,
): Promise<any> {
  const url = `${baseUrl}/api/SecurityDeposit/select-receiver/${listingId}`;

  const requestBody: SelectReceiverRequest = {
    votingPersonId: votingPersonId,
    selectedPersonId: selectedPersonId,
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to select receiver");
    }

    return await response.json();
  } catch (error) {
    console.error("Error selecting receiver:", error);
    throw error;
  }
}

export async function sendSecurityDepositReceiverEmail(
  listingId: string,
  baseUrlParam: string,
): Promise<any> {
  const url = `${baseUrl}/api/SecurityDepositEmail/SendSecurityDepositReceiverEmail`;
  const token = await getTokenFromLocalStorage();

  const requestBody = {
    listingId: listingId,
    baseUrl: baseUrlParam,
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });
    if (response.status === 401) {
      localStorage.clear();
      window.location.href = "/login";
      return;
    }
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || "Failed to send security deposit receiver emails",
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error sending security deposit receiver emails:", error);
    throw error;
  }
}

export async function sendSlotBookingEmail(
  listingId: string,
  baseUrlParam: string,
): Promise<any> {
  const url = `${baseUrl}/api/SlotBookingEmail/SendSlotBookingEmail`;
  const token = await getTokenFromLocalStorage();

  const requestBody = {
    listingId: listingId,
    baseUrl: baseUrlParam,
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });
    if (response.status === 401) {
      localStorage.clear();
      window.location.href = "/login";
      return;
    }
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to send emails");
    }
    return await response.json();
  } catch (error) {
    console.error("Error sending slot booking emails:", error);
    throw error;
  }
}

export async function saveSignature(
  listingId: string,
  personId: string,
  signatureData: string,
  selectedReceiverId: string,
): Promise<any> {
  const url = `${baseUrl}/api/SecurityDeposit/save-signature/${listingId}`;

  const requestBody = {
    personId: personId,
    signatureImage: signatureData,
    selectedReceiverId: selectedReceiverId,
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to save signature");
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving signature:", error);
    throw error;
  }
}

export async function checkAllSelectionsComplete(
  listingId: string,
): Promise<{ allCompleted: boolean; completedCount: number; totalCount: number; remainingCount: number }> {
  const url = `${baseUrl}/api/SecurityDeposit/check-all-selections/${listingId}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
      throw new Error(errorData.error || "Failed to check selections");
    }

    return await response.json();
  } catch (error) {
    console.error("Error checking all selections:", error);
    throw error;
  }
}

export async function generateAndSendPdf(listingId: string): Promise<any> {
  const url = `${baseUrl}/api/SecurityDeposit/generate-and-send-pdf/${listingId}`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
      throw new Error(errorData.error || "Failed to generate and send PDF");
    }

    return await response.json();
  } catch (error) {
    console.error("Error generating and sending PDF:", error);
    throw error;
  }
}
